import React from 'react'
import { graphql, Link } from 'gatsby'
import Image from 'gatsby-image'
import { useAtom } from 'jotai'
import dayjs from 'dayjs'
import { FormattedMessage } from 'react-intl'

import { userMenuAtom } from '~atoms'
import { getUser, getAgeGroupSlug } from '~utilities'

import Layout from '~components/layout'
import SEO from '~components/seo'
import Featured from '~components/home/featured'
import NavCards from '~components/home/nav-cards'
import NewAndTrending from '~components/home/new-and-trending'
import TrendingQuestions from '~components/home/trending-questions'
import SocialMedia from '~components/home/social-media'
import { YouTubeModal } from '~components/shared'

const Home = props => {
  const { section, locale } = props.pageContext
  const key = locale.toLowerCase()
  const { location } = props

  const { home, settings } = props.data
  const { facebookLink, twitterLink } = settings

  // @TODO This is only here to make sure the component rerenders when the user updates their profile. Replace with a more robust solution later.
  const [isUserMenuOpen] = useAtom(userMenuAtom)

  const user = getUser()
  const children = user && user?.['custom:childrenDOBs'] ? JSON.parse(user['custom:childrenDOBs']) : null

  let userAgeGroups = []
  if (children && children.length > 0) {
    children.map(child => {
      // Pipeline Operator: https://github.com/tc39/proposal-pipeline-operator#introduction
      // equivalent to: `getAgeGroupSlug(dayjs().diff(child, 'months'))`

      let ageGroup = dayjs().diff(child, 'months') |> getAgeGroupSlug
      userAgeGroups = userAgeGroups.concat([ageGroup])
    })
  }

  let title = ''
  let heroHeadline = ''
  let heroHeadlineLine2 = ''
  let heroImage = ''
  let heroNavCard1 = ''
  let heroNavCard2 = ''
  let heroNavCard3 = ''
  let heroNavCard4 = ''
  let mission = {}
  let missionImage = ''

  let newDocument = {
    ...home
  }

  if (newDocument.Title) {
    title = newDocument.Title
  }

  if (newDocument.Hero_Headline) {
    heroHeadline = newDocument.Hero_Headline
  }

  if (newDocument.Hero_Headline_Line_2) {
    heroHeadlineLine2 = ' ' + newDocument.Hero_Headline_Line_2
  }

  const heroFullHeadline = (
    <>
      {heroHeadline}
      {heroHeadlineLine2 && <br className='hidden lg:block' />}
      {heroHeadlineLine2 && heroHeadlineLine2}
    </>
  )

  const getHeroImage = () => {
    if (userAgeGroups.includes('newborn')) {
      return newDocument.heroImageNewborn.fluid
    } else if (userAgeGroups.includes('baby')) {
      return newDocument.heroImageBaby.fluid
    } else if (userAgeGroups.includes('toddler')) {
      return newDocument.heroImageToddler.fluid
    } else if (userAgeGroups.includes('preschooler')) {
      return newDocument.heroImagePreschooler.fluid
    } else {
      return newDocument.Hero_Image.fluid
    }
  }

  const getFeaturedContent = () => {
    if (userAgeGroups.includes('newborn')) {
      return newDocument.featuredContentNewborn.contentful_id
    } else if (userAgeGroups.includes('baby')) {
      return newDocument.featuredContentBaby.contentful_id
    } else if (userAgeGroups.includes('toddler')) {
      return newDocument.featuredContentToddler.contentful_id
    } else if (userAgeGroups.includes('preschooler')) {
      return newDocument.featuredContentPreschooler.contentful_id
    } else {
      return newDocument.FeaturedContent.contentful_id
    }
  }

  if (newDocument.Hero_Image.fluid) {
    heroImage = {
      fluid: { ...getHeroImage() },
      style: {
        maxHeight: 592,
        position: 'absolute'
      }
    }
  }

  if (newDocument.Mission_Headline && newDocument.Mission_CTALabel) {
    mission = {
      headline: newDocument.Mission_Headline,
      subheadline: newDocument.missionSubheadline,
      CTALabel: newDocument.Mission_CTALabel
    }
  }

  if (newDocument.Mission_BackgroundImage) {
    missionImage = newDocument.Mission_BackgroundImage.file.url
  }

  return (
    <Layout locale={locale} location={location} section={section}>
      <SEO
        lang={locale}
        title={home?.metaTitle || home.title}
        description={home?.metaDescription}
        // @TODO get article author and pass in
        // author={author.fullName}
        // keywords={[]} // @TODO figure out if we are doing keywords!?
        // @TODO do we want to include the og:type website is the current default.
      />
      <div className="bg-gray-light py-8 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="-ml-1 mb-8 lg:mb-12 font-extrabold text-3xl md:text-5xl leading-tight">
            {home.homeIntroHeading}
          </h2>

          <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-12 md:gap-6">

            <section className="bg-white shadow-xl md:col-span-12">
              <div className="flex flex-col md:flex-row">
                <div className="order-2 md:order-1 w-full md:w-1/2 p-6 md:p-10 md:border-t-8 border-purple">
                  <h2 className="text-xl md:text-4xl font-extrabold leading-none text-black mb-4">
                    {home.homePrimaryBox.cardTitle}
                  </h2>

                  <p className="text-base text-gray-800 leading-snug mb-8">
                    {home.homePrimaryBox.cardBody.cardBody}
                  </p>
                  <div className="flex justify-end">
                    <Link to={home.homePrimaryBox.cardLink} target="_blank" className="w-12 h-12 justify-center text-white hover:text-purple border border-purple bg-purple hover:bg-transparent rounded-full inline-flex items-center gap-4">
                      <i className="fas fa-caret-right text-3xl -mr-1" />
                    </Link>
                  </div>
                </div>

                <div className="order-1 md:order-2 w-full md:w-1/2 border-t-8 border-purple">
                  <img
                    src={home.homePrimaryBox.cardImage.file.url}
                    alt={home.homePrimaryBox.cardImage.description}
                    className="w-full h-100 md:h-full object-cover"
                  />
                </div>
              </div>
            </section>

            <div className="bg-white shadow-xl md:col-span-6">
              <div className="border-t-8 border-green flex flex-col md:flex-row">
                <img
                  src={home.homeSecondaryBox.cardImage.file.url}
                  alt={home.homeSecondaryBox.cardImage.description}
                  className="w-full md:w-1/2 h-48 md:h-56 object-cover"
                />

                <div className="w-full md:w-1/2 px-6 pt-6 md:py-0 inline-flex flex-col justify-center">
                  <h3 className="text-lg md:text-2xl font-extrabold leading-none text-black mb-3">
                    {home.homeSecondaryBox.cardTitle}
                  </h3>
                  <p className="text-base text-gray-800 leading-snug">
                    {home.homeSecondaryBox.cardBody.cardBody}
                  </p>
                </div>
              </div>

              <div className="p-4 md:p-6">
                <div className="gap-3 flex flex-col md:flex-row md:flex-wrap">
                {home.homeSecondaryBoxLinks.map((link, index) => (
                 <YouTubeModal
                    key={`home-video-link-${index}`}
                    videoId={link.externalLink}
                    buttonClassName="group px-4 py-1 border border-green-dark rounded-full inline-flex justify-between items-center gap-2 hover:bg-green-dark"
                    title={link.title}
                  >
                    <span className="text-sm leading-tight font-semibold text-black group-hover:text-white text-left">
                      {link.title}
                    </span>

                    <span className="ml-3 text-green-dark group-hover:text-white">
                      ▶
                    </span>
                  </YouTubeModal>

                ))}

                </div>
              </div>
            </div>
            <div className="flex flex-row gap-6 md:col-span-6">
              <div className="bg-white shadow-lg flex flex-col flex-1">
                <div className="border-t-8 border-orange">
                  <img
                    src={home.homeInfoBoxFirst.cardImage.file.url}
                    alt={home.homeInfoBoxFirst.cardImage.description}
                    className="w-full h-40 md:h-56 object-cover"
                  />
                </div>

                <div className="p-4 md:p-5 flex flex-col flex-1">
                  <h3 className="text-lg md:text-2xl font-extrabold leading-none text-black mb-3">
                    {home.homeInfoBoxFirst.cardTitle}
                  </h3>

                  <p className="text-base text-gray-800 leading-snug mb-6 flex-1">
                    {home.homeInfoBoxFirst.cardBody.cardBody}
                  </p>
                  <div className="flex justify-end">
                    <Link to={home.homeInfoBoxFirst.cardLink} className="w-12 h-12 justify-center text-white hover:text-orange border border-orange bg-orange hover:bg-transparent rounded-full inline-flex items-center gap-4">
                      <i className="fas fa-caret-right text-3xl -mr-1" />
                    </Link>
                  </div>
                </div>
              </div>

              <div className="bg-white shadow-lg flex flex-col flex-1">
                <div className="border-t-8 border-blue">
                  <img
                    src={home.homeInfoBoxSecond.cardImage.file.url}
                    alt={home.homeInfoBoxSecond.cardImage.description}
                    className="w-full h-40 md:h-56 object-cover"
                  />
                </div>

                <div className="p-4 md:p-5 flex flex-col flex-1">
                  <h3 className="text-lg md:text-2xl font-extrabold leading-none text-black mb-3">
                    {home.homeInfoBoxSecond.cardTitle}
                  </h3>

                  <p className="text-base text-gray-800 leading-snug mb-6 flex-1">
                    {home.homeInfoBoxSecond.cardBody.cardBody}
                  </p>

                  <div className="flex justify-end">
                    <Link to={home.homeInfoBoxSecond.cardLink} className="w-12 h-12 justify-center text-white hover:text-blue border border-blue bg-blue hover:bg-transparent rounded-full inline-flex items-center gap-4">
                      <i className="fas fa-caret-right text-3xl -mr-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='max-w-page py-10 md:py-16'>
        <div className='headline-section-centered mb-12'>
          <div>
            <FormattedMessage id='featured-this-month' />
          </div>
        </div>

        <div
          className="w-full bg-white shadow-center-xl flex flex-col justify-start items-start mx-auto"
          style={{ maxWidth: '900px'}}
        >
          <div className="w-full flex flex-col lg:flex-row justify-start items-start">
            <img
              className="w-full lg:w-1/2 self-stretch object-cover"
              src={home.homeFeaturedVideoCard.cardImage.file.url}
              alt={home.homeFeaturedVideoCard.cardImage.description}
            />

            <div
              className="w-full lg:w-1/2 flex flex-col justify-start items-start py-8"
            >
              <div
                className="w-full px-6 flex flex-col justify-start items-start gap-4"
              >
                <div
                  className="w-full text-blue font-extrabold font-montserrat text-2xl leading-7"
                >
                  {home.homeFeaturedVideoCard.cardTitle}
                </div>

                <div
                  className="w-full text-black text-base font-normal font-montserrat"
                >
                  {home.homeFeaturedVideoCard.cardBody.cardBody}
                </div>
              </div>

              <div
                className="px-6 flex flex-col justify-start items-start gap-4 mt-10 md:mt-6"
              >
                {home.homeFeaturedVideoCard.customVideoLinks.normalVideo?.videoId && (
                  <YouTubeModal
                    videoId={home.homeFeaturedVideoCard.customVideoLinks.normalVideo.videoId}
                    buttonClassName="bg-pink text-white font-extrabold font-montserrat inline-flex justify-center md:justify-start items-center py-4 px-5 gap-4 rounded-lg leading-5 text-lg border-2 border-pink hover:bg-transparent hover:text-pink w-full md:w-auto "
                    title={home.homeFeaturedVideoCard.customVideoLinks.normalVideo.buttonLabel}
                  >
                    <span>{home.homeFeaturedVideoCard.customVideoLinks.normalVideo.buttonLabel}</span>
                    <i className="fas fa-caret-right" />
                  </YouTubeModal>
                )}
                {home.homeFeaturedVideoCard.customVideoLinks.audioDescriptionVideo?.videoId && (
                  <YouTubeModal
                    videoId={home.homeFeaturedVideoCard.customVideoLinks.audioDescriptionVideo.videoId}
                    buttonClassName="text-pink font-extrabold font-montserrat inline-flex justify-center md:justify-start items-center bg-white py-4 px-5 gap-4 rounded-lg text-lg leading-5 border-2 border-pink hover:bg-pink hover:text-white w-full md:w-auto"
                    title={home.homeFeaturedVideoCard.customVideoLinks.audioDescriptionVideo.buttonLabel}
                  >
                    <span>{home.homeFeaturedVideoCard.customVideoLinks.audioDescriptionVideo.buttonLabel}</span>
                    <i className="fas fa-caret-right" />
                  </YouTubeModal>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='bg-cyan'>
        <div className='max-w-page md:pt-6 pt-20'>
          <div className='flex flex-col lg:flex-row items-center lg:items-end lg:px-32'>
            <div className='flex flex-col lg:w-7/12 lg:pb-32 gap-4'>
              <h3 className='self-stretch justify-start text-black text-4xl font-extrabold leading-10'>{mission.headline}</h3>
              <h4 className='self-stretch justify-start text-black text-xl font-extrabold leading-6 mb-6'>{mission.subheadline}</h4>
              <div>
                <div className='button button-blue'>
                  <Link to={`/${key}/about/`}>
                    <FormattedMessage id='learn-more' /> <em className='fas fa-caret-right ml-2' />
                  </Link>
                </div>
              </div>
            </div>
            <div className='lg:w-1/2 mt-4 lg:mt-0 lg:pl-8'>
              <img
                src={missionImage + '?fm=jpg&fl=progressive&w=320&q=80&bg=rgb:6fd2ff'}
                className='lg:hidden object-contain object-bottom'
                alt='N/A'
                aria-hidden
              />
              <img
                src={missionImage + '?fm=jpg&fl=progressive&w=960&q=80&bg=rgb:6fd2ff'}
                className='hidden lg:block lg:w-full lg:h-full object-contain object-bottom'
                alt='N/A'
                aria-hidden
              />
            </div>
          </div>
        </div>
      </div>
      <div className='wave-bottom-overlap' />
      <div className='max-w-page py-8'>
        <div className='headline-section-centered mt-24 mb-16'>
          <div>{user ? <FormattedMessage id='top-picks-for-you' /> : <FormattedMessage id='new-and-trending' />}</div>
          <hr aria-hidden />
        </div>
        <NewAndTrending />
      </div>
      <div className='bg-royal-purple pt-8 pb-10'>
        <div className='headline-section-royal-purple-centered'>
          <Link to={`/${key}/faqs/`}>
            <FormattedMessage id='trending-questions' />
          </Link>
          <hr aria-hidden />
        </div>
      </div>
      <TrendingQuestions />
      <div className='max-w-page py-8'>
        <div className='headline-section-centered mt-24 mb-16'>
          <div>
            <FormattedMessage id='stay-in-touch' />
          </div>
          <hr aria-hidden />
        </div>
        <SocialMedia />
        <div className='headline-6 mt-8 text-center'>
          {locale === 'en-US' ? 'We’re on' : '¡También estamos en'}{' '}
          <a href={facebookLink} target='_blank' rel='noreferrer noopener' className='text-blue'>
            <em className='fab fa-facebook-square' /> Facebook
          </a>
          {locale === 'en-US' ? ' and ' : ' y '}
          <a href={twitterLink} target='_blank' rel='noopener noreferrer' className='text-blue'>
            <em className='fab fa-twitter-square' /> Twitter
          </a>
          {locale === 'en-US' ? ' too!' : '!'}
        </div>
      </div>
    </Layout>
  )
}

export default Home

export const homePageQuery = graphql`
  query homePageQuery($locale: String!) {
    home: contentfulHome(node_locale: { eq: $locale }) {
      Title
      metaTitle
      metaDescription
      homeIntroHeading
      homePrimaryBox {
        cardTitle
        cardBody {
          cardBody
        }
        cardImage {
          file {
            url
          }
          description
        }
        cardLink
      }
      homeSecondaryBox {
        cardTitle
        cardBody {
          cardBody
        }
        cardImage {
          file {
            url
          }
          description
        }
        cardLink
      }
      homeSecondaryBoxLinks {
        title
        externalLink
      }
      homeInfoBoxFirst {
        cardTitle
        cardBody {
          cardBody
        }
        cardImage {
          file {
            url
          }
          description
        }
        cardLink
      }
      homeInfoBoxSecond {
        cardTitle
        cardBody {
          cardBody
        }
        cardImage {
          file {
            url
          }
          description
        }
        cardLink
      }
      homeFeaturedVideoCard {
        cardTitle
        cardBody {
          cardBody
        }
        cardImage {
          file {
            url
          }
          description
        }
        cardLink
        customVideoLinks {
          audioDescriptionVideo {
            buttonLabel
            videoId
          }
          normalVideo {
            buttonLabel
            videoId
          }
        }
      }
      Hero_Headline
      Hero_Headline_Line_2
      Hero_Image {
        fluid(maxWidth: 1440, quality: 80) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      heroImageNewborn {
        fluid(maxWidth: 1440, quality: 80) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      heroImageBaby {
        fluid(maxWidth: 1440, quality: 80) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      heroImageToddler {
        fluid(maxWidth: 1440, quality: 80) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      heroImagePreschooler {
        fluid(maxWidth: 1440, quality: 80) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      Hero_NavCard1_Headline
      Hero_NavCard1_Subline
      Hero_NavCard1_ExternalLink
      Hero_NavCard2_Headline
      Hero_NavCard2_Subline
      Hero_NavCard2_InternalLink {
        internal {
          type
        }
        node_locale
        slug
        type
      }
      Hero_NavCard3_Headline
      Hero_NavCard3_Subline
      Hero_NavCard3_InternalLink {
        internal {
          type
        }
        node_locale
        slug
      }
      Hero_NavCard4_Headline
      Hero_NavCard4_Subline
      Hero_NavCard4_InternalLink {
        internal {
          type
        }
        node_locale
        slug
        type
      }
      Mission_Headline
      missionSubheadline
      Mission_CTALabel
      Mission_BackgroundImage {
        file {
          url
        }
      }
      FeaturedContent {
        contentful_id
      }
      featuredContentNewborn {
        contentful_id
      }
      featuredContentBaby {
        contentful_id
      }
      featuredContentToddler {
        contentful_id
      }
      featuredContentPreschooler {
        contentful_id
      }
    }
    settings: contentfulSettings(title: { eq: "Global Settings" }) {
      facebookLink
      twitterLink
    }
  }
`
